import { Map, List } from 'immutable'
import { get } from 'lodash'
import { createSelector } from 'reselect'
import { makeUserSelector, availableUsers, awayUsers } from 'app/modules/user/selectors'
import { orgTimezone } from 'app/modules/embed/selectors'

import { AvailabilitySlot, AvailabilitySlotTime } from 'app/modules/meetings/models'

import * as constants from './constants'

export const teams = state => state.getIn(['teams', 'data']) || []

export const teamAvailabilities = state => state.getIn(['teams', 'teamAvailabilities']) || []
export const teamsOptions = state => {
  return teams(state).map(team => {
    return {
      label: team.name,
      value: team,
    }
  })
}
export const teamsStatus = state => state.getIn(['teams', 'status'])

export const makeTeamSelector = () =>
  createSelector([(state, props) => props.teamId, teams], (teamId, teams) =>
    teams.find(team => team.id === teamId)
  )

export const fetchTeamsSuccess = createSelector(
  state => state.getIn(['teams', 'status', 'fetch']),
  teamFetchStatus => teamFetchStatus === constants.FETCH_ORG_TEAMS_SUCCESS
)

export const teamsForUser = userId => state => state.getIn(['currentUser', 'orgUsers', userId, 'teams']) || []

export const fetchTeamsPending = createSelector(
  state => state.getIn(['teams', 'status', 'fetch']),
  teamFetchStatus => teamFetchStatus === constants.FETCH_ORG_TEAMS_PENDING
)

export const fetchTeamsFailed = createSelector(
  state => state.getIn(['teams', 'status', 'fetch']),
  teamFetchStatus => teamFetchStatus === constants.FETCH_ORG_TEAMS_FAILED
)

export const createTeamFailed = createSelector(
  state => state.getIn(['teams', 'status', 'create']),
  teamFetchStatus => teamFetchStatus === constants.CREATE_ORG_TEAM_FAILED
)

export const createTeamSucceeded = createSelector(
  state => state.getIn(['teams', 'status', 'create']),
  teamFetchStatus => teamFetchStatus === constants.CREATE_ORG_TEAM_SUCCESS
)

export const patchTeamFailed = createSelector(
  state => state.getIn(['teams', 'status', 'update']),
  patchStatus => patchStatus === constants.PATCH_ORG_TEAM_FAILED
)
export const patchTeamPending = createSelector(
  state => state.getIn(['teams', 'status', 'update']),
  patchStatus => patchStatus === constants.PATCH_ORG_TEAM_PENDING
)
export const patchTeamSucceeded = createSelector(
  state => state.getIn(['teams', 'status', 'update']),
  patchStatus => patchStatus === constants.PATCH_ORG_TEAM_SUCCESS
)

export const deleteTeamFailed = createSelector(
  state => state.getIn(['teams', 'status', 'delete']),
  deleteStatus => deleteStatus === constants.DELETE_ORG_TEAM_FAILED
)
export const deleteTeamSucceeded = createSelector(
  state => state.getIn(['teams', 'status', 'delete']),
  deleteStatus => deleteStatus === constants.DELETE_ORG_TEAM_SUCCESS
)

export const updateAvailabilityForTeamSucceeded = createSelector(
  state => state.getIn(['teams', 'status', 'updateAvailabilityForTeam']),
  updateStatus => updateStatus === constants.UPDATE_AVAILABILITY_FOR_TEAM_SUCCESS
)
export const updateAvailabilityForTeamPending = createSelector(
  state => state.getIn(['teams', 'status', 'updateAvailabilityForTeam']),
  updateStatus => updateStatus === constants.UPDATE_AVAILABILITY_FOR_TEAM_PENDING
)
export const updateAvailabilityForTeamFailed = createSelector(
  state => state.getIn(['teams', 'status', 'updateAvailabilityForTeam']),
  updateStatus => updateStatus === constants.UPDATE_AVAILABILITY_FOR_TEAM_FAILED
)

export const fetchTeamAvailabilitiesSucceeded = createSelector(
  state => state.getIn(['teams', 'status', 'fetchAllAvailabilities']),
  teamFetchStatus => teamFetchStatus === constants.FETCH_TEAM_AVAILABILITIES_SUCCESS
)
export const fetchTeamAvailabilitiesPending = createSelector(
  state => state.getIn(['teams', 'status', 'fetchAllAvailabilities']),
  teamFetchStatus => teamFetchStatus === constants.FETCH_TEAM_AVAILABILITIES_PENDING
)
export const fetchTeamAvailabilitiesFailed = createSelector(
  state => state.getIn(['teams', 'status', 'fetchAllAvailabilities']),
  teamFetchStatus => teamFetchStatus === constants.FETCH_TEAM_AVAILABILITIES_FAILED
)

export const fetchTeamAvailabilitySucceeded = createSelector(
  state => state.getIn(['teams', 'status', 'fetchSingleAvailability']),
  teamFetchStatus => teamFetchStatus === constants.FETCH_TEAM_AVAILABILITY_SUCCESS
)
export const fetchTeamAvailabilityPending = createSelector(
  state => state.getIn(['teams', 'status', 'fetchSingleAvailability']),
  teamFetchStatus => teamFetchStatus === constants.FETCH_TEAM_AVAILABILITY_PENDING
)
export const fetchTeamAvailabilityFailed = createSelector(
  state => state.getIn(['teams', 'status', 'fetchSingleAvailability']),
  teamFetchStatus => teamFetchStatus === constants.FETCH_TEAM_AVAILABILITY_FAILED
)

export const createTeamAvailabilitySucceeded = createSelector(
  state => state.getIn(['teams', 'status', 'createTeamAvailability']),
  teamFetchStatus => teamFetchStatus === constants.CREATE_TEAM_AVAILABILITY_SUCCESS
)
export const createTeamAvailabilityPending = createSelector(
  state => state.getIn(['teams', 'status', 'createTeamAvailability']),
  teamFetchStatus => teamFetchStatus === constants.CREATE_TEAM_AVAILABILITY_PENDING
)
export const createTeamAvailabilityFailed = createSelector(
  state => state.getIn(['teams', 'status', 'createTeamAvailability']),
  teamFetchStatus => teamFetchStatus === constants.CREATE_TEAM_AVAILABILITY_FAILED
)

export const updateTeamAvailabilitySucceeded = createSelector(
  state => state.getIn(['teams', 'status', 'updateTeamAvailability']),
  teamFetchStatus => teamFetchStatus === constants.UPDATE_TEAM_AVAILABILITY_SUCCESS
)
export const updateTeamAvailabilityPending = createSelector(
  state => state.getIn(['teams', 'status', 'updateTeamAvailability']),
  teamFetchStatus => teamFetchStatus === constants.UPDATE_TEAM_AVAILABILITY_PENDING
)
export const updateTeamAvailabilityFailed = createSelector(
  state => state.getIn(['teams', 'status', 'updateTeamAvailability']),
  teamFetchStatus => teamFetchStatus === constants.UPDATE_TEAM_AVAILABILITY_FAILED
)

export const teamMembers = createSelector(
  [state => state, (state, props) => props.teamId, makeTeamSelector, makeUserSelector],
  (state, teamId, teamSelector, userSelector) => {
    const foundTeam = teamSelector(state, { teamId })
    const memberIds = get(foundTeam, 'members', [])
    return memberIds.map(id => userSelector(state, { id }).toJS())
  }
)

export const teamOwners = createSelector(
  [state => state, teams, makeUserSelector],
  (state, teams, userSelector) =>
    teams.map(({ id, owner }) => ({
      id,
      owner: userSelector(state, { id: owner }).toJS(),
    }))
)

export const teamOwner = teamId => state => {
  // this selector smells bad, toJS and not createSelector
  const userSelector = makeUserSelector()
  const foundTeam = makeTeamSelector()(state, { teamId })
  return userSelector(state, { id: foundTeam.owner }).toJS()
}

export const onlineTeamMembers = teamId => state => {
  const members = teamMembers(state, { teamId })
  return members.filter(member => member.availability !== 'OFFLINE')
}

export const offlineTeamMembers = teamId => state => {
  const members = teamMembers(state, { teamId })
  return members.filter(member => member.availability === 'OFFLINE')
}

export const teamsWithMemberAvailability = createSelector(
  teams,
  awayUsers,
  availableUsers,
  (teams, awayUsers, availableUsers) => {
    return teams.map(team => {
      return {
        ...team,
        awayMembers: awayUsers.filter(user => team.members.includes(user.id)),
        availableMembers: availableUsers.filter(user => team.members.includes(user.id)),
      }
    })
  }
)

export const makeDefaultAvailabilityForTeam = teamName => state => {
  const timezone = orgTimezone(state)
  return Map({
    name: `${teamName} Availability`,
    timezone,
    openHours: List([
      AvailabilitySlot({
        dayOfWeek: 'WEEKDAYS',
        startTime: AvailabilitySlotTime({
          hour: 9,
          minutes: 0,
        }),
        endTime: AvailabilitySlotTime({
          hour: 17,
          minutes: 0,
        }),
      }),
    ]),
  })
}



// WEBPACK FOOTER //
// ./src/scripts/app/modules/teams/selectors.js